import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { generateProfilePictureSVG, generateGradientFromUsername } from '../utils/profilePicture'
import './UserProfile.css'

interface UserPost {
  id: string
  title: string
  content: string
  createdAt: string
  likesCount: number
  commentsCount: number
}

interface UserCreation {
  id: string
  title: string
  description: string
  category: string
  createdAt: string
  likesCount: number
}

const UserProfile = () => {
  const { username } = useParams<{ username: string }>()
  const [activeTab, setActiveTab] = useState<'posts' | 'creations'>('posts')
  const [userPosts, setUserPosts] = useState<UserPost[]>([])
  const [userCreations, setUserCreations] = useState<UserCreation[]>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [userExists, setUserExists] = useState(false)

  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) {
        setLoading(false)
        return
      }

      // Get profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, is_admin')
        .eq('username', username)
        .single()

      if (!profile) {
        setLoading(false)
        return
      }

      setUserExists(true)
      setIsAdmin(profile.is_admin || false)

      // Fetch posts
      const { data: postsData } = await supabase
        .from('forum_posts')
        .select('id, title, content, created_at')
        .eq('author_id', profile.id)
        .order('created_at', { ascending: false })

      const { data: postLikes } = await supabase
        .from('forum_post_likes')
        .select('post_id')

      const { data: comments } = await supabase
        .from('forum_comments')
        .select('post_id')

      const posts: UserPost[] = (postsData || []).map((p: Record<string, unknown>) => ({
        id: p.id as string,
        title: p.title as string,
        content: p.content as string,
        createdAt: p.created_at as string,
        likesCount: (postLikes || []).filter((l: Record<string, unknown>) => l.post_id === p.id).length,
        commentsCount: (comments || []).filter((c: Record<string, unknown>) => c.post_id === p.id).length,
      }))
      setUserPosts(posts)

      // Fetch creations
      const { data: creationsData } = await supabase
        .from('creations')
        .select('id, title, description, category, created_at')
        .eq('author_id', profile.id)
        .order('created_at', { ascending: false })

      const { data: creationLikes } = await supabase
        .from('creation_likes')
        .select('creation_id')

      const creations: UserCreation[] = (creationsData || []).map((c: Record<string, unknown>) => ({
        id: c.id as string,
        title: c.title as string,
        description: (c.description as string) || '',
        category: (c.category as string) || 'audio',
        createdAt: c.created_at as string,
        likesCount: (creationLikes || []).filter((l: Record<string, unknown>) => l.creation_id === c.id).length,
      }))
      setUserCreations(creations)

      setLoading(false)
    }

    fetchUserData()
  }, [username])

  if (loading) {
    return (
      <main className="main-content user-profile-page">
        <div className="user-profile-container">
          <p>loading...</p>
        </div>
      </main>
    )
  }

  if (!userExists) {
    return (
      <main className="main-content user-profile-page">
        <div className="user-profile-container">
          <h1>user not found</h1>
          <Link to="/community" className="user-profile-back-link">
            back to community
          </Link>
        </div>
      </main>
    )
  }

  return (
    <main className="main-content user-profile-page">
      <div className="user-profile-container">
        <Link to="/community" className="user-profile-back-link">
          <i className="fas fa-arrow-left"></i>
          back to community
        </Link>

        <div className="user-profile-header">
          <div
            className="user-profile-avatar"
            style={{ background: generateGradientFromUsername(username || '') }}
            dangerouslySetInnerHTML={{ __html: generateProfilePictureSVG(username || '', 120) }}
          />
          <div className="user-profile-info">
            <h1 className="user-profile-username">
              {username}
              {isAdmin && <span className="user-profile-admin-badge">admin</span>}
            </h1>
            <div className="user-profile-stats">
              <div className="user-profile-stat">
                <span className="user-profile-stat-value">{userPosts.length}</span>
                <span className="user-profile-stat-label">posts</span>
              </div>
              <div className="user-profile-stat">
                <span className="user-profile-stat-value">{userCreations.length}</span>
                <span className="user-profile-stat-label">creations</span>
              </div>
            </div>
          </div>
        </div>

        <div className="user-profile-tabs">
          <button
            className={`user-profile-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            posts
          </button>
          <button
            className={`user-profile-tab ${activeTab === 'creations' ? 'active' : ''}`}
            onClick={() => setActiveTab('creations')}
          >
            creations
          </button>
        </div>

        <div className="user-profile-content">
          {activeTab === 'posts' && (
            <div className="user-profile-section">
              <h2>posts</h2>
              {userPosts.length === 0 ? (
                <div className="user-profile-empty">
                  <p>no posts yet</p>
                </div>
              ) : (
                <div className="user-profile-posts">
                  {userPosts.map((post) => (
                    <Link key={post.id} to={`/community?tab=forum&post=${post.id}`} className="user-profile-post-card">
                      <h3>{post.title}</h3>
                      <p>{post.content.substring(0, 150)}...</p>
                      <div className="user-profile-post-meta">
                        <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                        <span>{post.likesCount} likes</span>
                        <span>{post.commentsCount} comments</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'creations' && (
            <div className="user-profile-section">
              <h2>creations</h2>
              {userCreations.length === 0 ? (
                <div className="user-profile-empty">
                  <p>no creations yet</p>
                </div>
              ) : (
                <div className="user-profile-creations">
                  {userCreations.map((creation) => (
                    <Link key={creation.id} to={`/creation/${creation.id}`} className="user-profile-creation-card">
                      <h3>{creation.title}</h3>
                      <p>{creation.description.substring(0, 150)}...</p>
                      <div className="user-profile-creation-meta">
                        <span className="user-profile-creation-category">{creation.category}</span>
                        <span>{creation.likesCount} likes</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default UserProfile
